import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { showService } from '../services/endpoints/showService';
import { seasonService } from '../services/endpoints/seasonService';
import { parseLocalDate } from '../utils/dateUtils';
import ShowBudget from '../components/budget/ShowBudget';
import { useCoachView } from '../contexts/CoachViewContext';

interface Phase {
  phaseId?: string;
  name: string;
  phaseType: string;
  startDate: string;
  endDate: string;
}

interface Show {
  showId?: string;
  name: string;
  date: string;
  location: string;
  category: string;
  division?: string;
  targetWeight?: number;
  phases?: Phase[];
  // Season support (v4-dev)
  seasonId?: string;
  showPriority?: string;
}

// Map phaseType values to friendly display labels
const phaseTypeLabels: Record<string, string> = {
  cruise: 'Cruise',
  miniCut: 'Mini Cut',
  hold: 'Hold',
  bridge: 'Bridge',
  rebound: 'Rebound',
  growthBlast: 'Growth Blast',
  contestPrep: 'Contest Prep',
  show: 'Show',
  // PascalCase variants (for newly added phases before save)
  Cruise: 'Cruise',
  MiniCut: 'Mini Cut',
  Hold: 'Hold',
  Bridge: 'Bridge',
  Rebound: 'Rebound',
  GrowthBlast: 'Growth Blast',
  ContestPrep: 'Contest Prep',
  Show: 'Show',
};

const showPriorityLabels: Record<string, string> = {
  Primary: 'Primary',
  Secondary: 'Secondary',
  Backup: 'Backup',
  TuneUp: 'Tune-Up',
  Added: 'Added',
};

const ShowManagement: React.FC = () => {
  const { viewAs, viewAsName, isCoachView } = useCoachView();
  const queryClient = useQueryClient();

  const [shows, setShows] = useState<Show[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingShow, setEditingShow] = useState<Show | null>(null);

  // Inline delete confirmation — stores the showId awaiting confirmation
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const [formData, setFormData] = useState<Show>({
    name: '',
    date: '',
    location: '',
    category: '',
    division: '',
    targetWeight: undefined,
    seasonId: undefined,
    showPriority: 'Primary',
  });

  // Load available seasons for the season dropdown
  const { data: seasons = [] } = useQuery({
    queryKey: ['seasons'],
    queryFn: () => seasonService.getSeasons(),
    enabled: !isCoachView, // only for the athlete's own view
  });

  useEffect(() => {
    loadShows();
  }, [viewAs]); // re-load when coach switches athletes

  const loadShows = async () => {
    try {
      setLoading(true);
      const data = await showService.getShows(viewAs || undefined);
      setShows(data);
      setError(null);
    } catch (err) {
      setError('Failed to load shows');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingShow?.showId) {
        await showService.updateShow(editingShow.showId, formData as any);
      } else {
        await showService.createShow(formData as any);
      }
      await loadShows();
      queryClient.invalidateQueries({ queryKey: ['dashboard'] }); // refresh dashboard season data
      resetForm();
      setError(null);
    } catch (err) {
      setError('Failed to save show');
      console.error(err);
    }
  };

  const handleDeleteRequest = (id: string) => {
    setConfirmDeleteId(id);
  };

  const handleDeleteConfirm = async (id: string) => {
    try {
      await showService.deleteShow(id);
      setConfirmDeleteId(null);
      await loadShows();
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setError(null);
    } catch (err) {
      setError('Failed to delete show');
      console.error(err);
    }
  };

  const handleDeleteCancel = () => {
    setConfirmDeleteId(null);
  };

  const handleEdit = (show: Show) => {
    setEditingShow(show);
    setFormData({ ...show, date: show.date.split('T')[0] });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({ name: '', date: '', location: '', category: '', division: '', targetWeight: undefined, seasonId: undefined, showPriority: 'Primary' });
    setEditingShow(null);
    setShowForm(false);
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gold-500 mb-2">Show Management</h1>
          <p className="text-gray-400">
            {isCoachView
              ? `Viewing ${viewAsName ?? 'athlete'}'s shows — read only`
              : 'Manage your competition shows'}
          </p>
        </div>
        {!isCoachView && !showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="bg-gold-500 hover:bg-gold-600 text-black font-bold py-2 px-6 rounded transition-colors"
          >
            + Create Show
          </button>
        )}
      </div>

      {isCoachView && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-blue-900/30 border border-blue-600/40 text-blue-300 text-sm flex items-center gap-2">
          👁️ Coach view — you can see {viewAsName ?? 'this athlete'}'s shows but cannot make changes.
        </div>
      )}

      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {showForm && !isCoachView && (
        <div className="bg-dark-800 rounded-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gold-500">
              {editingShow ? 'Edit Show' : 'Create New Show'}
            </h2>
            <button
              onClick={resetForm}
              className="text-gray-400 hover:text-white"
            >
              ✕ Cancel
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Show Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-dark-700 border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:border-gold-500"
                  placeholder="2027 Ronnie Coleman Classic"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Show Date *
                </label>
                <input
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full bg-dark-700 border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:border-gold-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Location *
                </label>
                <input
                  type="text"
                  required
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full bg-dark-700 border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:border-gold-500"
                  placeholder="Dallas, TX"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Category *
                </label>
                <select
                  required
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full bg-dark-700 border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:border-gold-500"
                >
                  <option value="">Select Category</option>
                  <option value="Classic Physique">Classic Physique</option>
                  <option value="Men's Physique">Men's Physique</option>
                  <option value="Bodybuilding">Bodybuilding</option>
                  <option value="Women's Physique">Women's Physique</option>
                  <option value="Bikini">Bikini</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Division
                </label>
                <input
                  type="text"
                  value={formData.division}
                  onChange={(e) => setFormData({ ...formData, division: e.target.value })}
                  className="w-full bg-dark-700 border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:border-gold-500"
                  placeholder="Masters 50+"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Target Weight (lbs)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.targetWeight || ''}
                  onChange={(e) => setFormData({ ...formData, targetWeight: parseFloat(e.target.value) || undefined })}
                  className="w-full bg-dark-700 border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:border-gold-500"
                  placeholder="182.0"
                />
              </div>

              {/* Season support fields (v4-dev) */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Season <span className="text-gray-500 font-normal">(optional)</span>
                </label>
                <select
                  value={formData.seasonId ?? ''}
                  onChange={(e) => setFormData({ ...formData, seasonId: e.target.value || undefined })}
                  className="w-full bg-dark-700 border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:border-gold-500"
                >
                  <option value="">— No season —</option>
                  {seasons.map(s => (
                    <option key={s.seasonId} value={s.seasonId}>{s.name}</option>
                  ))}
                </select>
                {seasons.length === 0 && (
                  <p className="text-xs text-gray-600 mt-1">No seasons yet — create one via the API to link shows.</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Show Priority
                </label>
                <select
                  value={formData.showPriority ?? 'Primary'}
                  onChange={(e) => setFormData({ ...formData, showPriority: e.target.value })}
                  className="w-full bg-dark-700 border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:border-gold-500"
                >
                  <option value="Primary">Primary</option>
                  <option value="Secondary">Secondary</option>
                  <option value="Backup">Backup</option>
                  <option value="TuneUp">Tune-Up</option>
                  <option value="Added">Added</option>
                </select>
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <button
                type="submit"
                className="bg-gold-500 hover:bg-gold-600 text-black font-bold py-2 px-6 rounded transition-colors"
              >
                {editingShow ? 'Update Show' : 'Create Show'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-6 rounded transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Shows List */}
      <div className="space-y-4">
        {shows.length === 0 ? (
          <div className="bg-dark-800 rounded-lg p-8 text-center">
            <p className="text-gray-400 mb-2">No shows yet</p>
            <p className="text-sm text-gray-500">Create your first show to get started!</p>
          </div>
        ) : (
          shows.map((show) => (
            <div key={show.showId} className="bg-dark-800 rounded-lg p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-2xl font-bold text-gold-500">{show.name}</h3>
                    {show.showPriority && show.showPriority !== 'Primary' && (
                      <span className="px-2 py-0.5 text-xs rounded bg-dark-700 text-gray-400 border border-gray-600">
                        {showPriorityLabels[show.showPriority] ?? show.showPriority}
                      </span>
                    )}
                  </div>
                  <p className="text-gray-400">{show.location} • {parseLocalDate(show.date).toLocaleDateString()}</p>
                </div>
                <div className="flex gap-2">
                  {!isCoachView && (
                    <>
                      <button
                        onClick={() => handleEdit(show)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1 rounded text-sm"
                      >
                        Edit
                      </button>
                      {confirmDeleteId === show.showId ? (
                        <div className="flex gap-1 items-center">
                          <button
                            onClick={() => handleDeleteConfirm(show.showId!)}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm font-semibold"
                          >
                            Confirm Delete
                          </button>
                          <button
                            onClick={handleDeleteCancel}
                            className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleDeleteRequest(show.showId!)}
                          className="bg-red-600 hover:bg-red-700 text-white px-4 py-1 rounded text-sm"
                        >
                          Delete
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">Category:</span>
                  <span className="text-white ml-2">{show.category}</span>
                </div>
                {show.division && (
                  <div>
                    <span className="text-gray-400">Division:</span>
                    <span className="text-white ml-2">{show.division}</span>
                  </div>
                )}
                {show.targetWeight && (
                  <div>
                    <span className="text-gray-400">Target Weight:</span>
                    <span className="text-white ml-2">{show.targetWeight} lbs</span>
                  </div>
                )}
                <div>
                  <span className="text-gray-400">Phases:</span>
                  <span className="text-white ml-2">{show.phases?.length || 0}</span>
                </div>
              </div>

              {show.phases && show.phases.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <h4 className="text-sm font-semibold text-gray-300 mb-2">Phases:</h4>
                  <div className="space-y-2">
                    {[...show.phases].sort((a, b) => a.startDate.localeCompare(b.startDate)).map((phase, index) => (
                      <div key={index} className="text-sm text-gray-400">
                        <span className="text-gold-400 font-medium">{phase.name}</span>
                        <span className="mx-2">•</span>
                        <span>{phaseTypeLabels[phase.phaseType] || phase.phaseType}</span>
                        <span className="mx-2">•</span>
                        <span>{parseLocalDate(phase.startDate).toLocaleDateString()} - {parseLocalDate(phase.endDate).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Show Budget — hidden in coach view (financial data is private) */}
              {show.showId && !isCoachView && (
                <div className="mt-5 pt-4 border-t border-gray-700">
                  <ShowBudget showId={show.showId} />
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ShowManagement;
